/**
 * ReportFilterStateContext
 *
 * Holds the user's selected filter values for the entire reporting section
 * and keeps them alive across tab switches. Mount
 * <ReportFilterStateProvider> once at the ReportingView level so
 * selected filters carry over when the user switches between report tabs.
 *
 * Filter fields and date ranges are configuration-driven, allowing
 * org admins to customize which filters are available without code changes.
 * Filter state is created dynamically based on enabledFields and field types.
 */

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import {
  initializeFilterState,
  isDateRangeFilter,
  writeFilterToSession,
  clearReportFiltersFromSession,
} from "./utils";
import type {
  ReportFilterStateContextValue,
  DynamicFilterState,
  FilterStateSetter,
  FilterStateValue,
} from "./types";

export type {
  ReportFilterStateContextValue,
  DynamicFilterState,
  FilterStateSetter,
  FilterStateValue,
} from "./types";

const DEFAULT_STATE: ReportFilterStateContextValue = {
  filterState: {},
  createFilterSetter: () => () => {},
  clearAll: () => {},
};

const ReportFilterStateContext =
  createContext<ReportFilterStateContextValue>(DEFAULT_STATE);

interface ReportFilterStateProviderProps extends PropsWithChildren {
  enabledFields?: readonly string[];
  reportId?: string;
}

export const ReportFilterStateProvider = ({
  children,
  enabledFields: enabledFieldsProp,
  reportId,
}: ReportFilterStateProviderProps) => {
  const enabledFields = useMemo(() => enabledFieldsProp ?? [], [enabledFieldsProp]);
  const { config } = useAssetsConfigContext();

  const [filterState, setFilterStateRaw] = useState<DynamicFilterState>(() =>
    initializeFilterState(Array.from(enabledFields), config, reportId)
  );

  const enabledFieldsKey = enabledFields.join(",");

  useEffect(() => {
    if (Object.keys(config).length > 0) {
      const newState = initializeFilterState(Array.from(enabledFields), config, reportId);
      setFilterStateRaw(newState);
    }
    // enabledFieldsKey is the stable proxy for enabledFields by content, avoiding resets on new-but-equal prop references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFieldsKey, config, reportId]);

  const createFilterSetter = useCallback(
    (columnName: string): FilterStateSetter =>
      (value: FilterStateValue) => {
        if (reportId) writeFilterToSession(reportId, columnName, value);
        setFilterStateRaw((prev) => ({ ...prev, [columnName]: value }));
      },
    [reportId]
  );

  const clearAll = useCallback(() => {
    if (reportId) clearReportFiltersFromSession(reportId);
    const clearedState: DynamicFilterState = {};
    enabledFields.forEach((columnName) => {
      const fieldConfig = config[columnName];
      if (isDateRangeFilter(fieldConfig)) {
        clearedState[columnName] = { start: null, end: null };
      } else {
        clearedState[columnName] = [];
      }
    });
    setFilterStateRaw(clearedState);
  }, [enabledFields, config, reportId]);

  const value = useMemo<ReportFilterStateContextValue>(
    () => ({ filterState, createFilterSetter, clearAll }),
    [filterState, createFilterSetter, clearAll]
  );

  return (
    <ReportFilterStateContext.Provider value={value}>
      {children}
    </ReportFilterStateContext.Provider>
  );
};

export const useReportFilterStateContext =
  (): ReportFilterStateContextValue =>
    useContext(ReportFilterStateContext);
